// Tight Binding → Wannier functions illustrative projects (examples/wannier/): finished chain runs (a DFT run with its
// charge density kept, and route A from it), listed under the Slater-Koster ones on the Tight Binding gallery. Opening
// one gives a Wannier project of the user's own with both runs copied in; "try this" makes a new project with the
// recipe changed and its own copy of the DFT run (no new SCF: the nscf, pw2wannier90.x and wannier90.x run).
import { Accordion, Alert, Anchor, Badge, Button, Card, Loader, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { ErrorBox } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { useMe } from "../lib/hooks";
import { openExample, useExample, type Example } from "../lib/examples";
import { examplesPath } from "../lib/modules";
import { fmtMeV } from "../lib/wannier";
import { TryList } from "./DftExamples";
import { Specialities } from "./Examples";

export function wannierResultLine(r: Example["result"]): string {
  if (!r || r.num_wann == null) return "";
  const parts = [`${r.num_wann} functions`];
  if (r.omega_A2 != null) parts.push(`Ω = ${r.omega_A2.toFixed(2)} Å²`);
  if (r.band_error_rms_eV != null) parts.push(`${fmtMeV(r.band_error_rms_eV)} RMS`);
  return parts.join(" · ");
}

export function WannierExamplesTable({ examples }: { examples?: Example[] }) {
  const nav = useNavigate();
  const data = (examples ?? []).filter((e) => e.product === "wannier");
  if (!data.length) return null;
  return (
    <>
      <Text fw={600} mt="lg" mb={4}>Wannier functions</Text>
      <Text size="sm" c="dimmed" mb="sm" maw={900}>Finished Wannier90 runs from a DFT run of PRAYOG-QP: disentanglement, hybrids, a metal.
        Open one to look at the model, its spreads and its quality report; "try this" changes one thing and runs again from the
        same DFT run.</Text>
      <Card withBorder p={0}>
        <Table.ScrollContainer minWidth={700}>
          <Table highlightOnHover verticalSpacing="sm" data-testid="wannier-examples-table">
            <Table.Thead>
              <Table.Tr><Table.Th>#</Table.Th><Table.Th>Project</Table.Th><Table.Th>Material</Table.Th><Table.Th>Speciality</Table.Th>
                <Table.Th>Result</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((e, i) => (
                <Table.Tr key={e.id} style={{ cursor: "pointer" }} onClick={() => nav(examplesPath("tight-binding", e.id))}>
                  <Table.Td>{i + 1}</Table.Td>
                  <Table.Td><Link to={examplesPath("tight-binding", e.id)} onClick={(ev) => ev.stopPropagation()}><Text fw={600} size="sm">{e.name}</Text></Link>
                    {e.project_id && <Badge size="xs" color="teal" variant="light" ml={6}>opened</Badge>}</Table.Td>
                  <Table.Td><Text size="sm">{e.material}</Text></Table.Td>
                  <Table.Td><Specialities items={e.speciality} /></Table.Td>
                  <Table.Td><Text size="sm">{wannierResultLine(e.result)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </>
  );
}

function OpenButton({ ex }: { ex: Example }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: me } = useMe();
  const allowed = !!me?.limits["wannier.enabled"];
  const open = useMutation({
    mutationFn: () => openExample(ex.id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["examples"] });
      nav(`/projects/${p.id}`);
    },
  });
  return (
    <Stack gap={4} align="flex-start">
      <Button onClick={() => open.mutate()} loading={open.isPending} disabled={!allowed} data-testid="open-wannier-example">
        {ex.project_id ? "Continue in my project" : "Open as my project"}</Button>
      {!allowed && <Text size="xs" c="orange">Wannier functions are not part of your plan.</Text>}
      <ErrorBox error={open.error} />
    </Stack>
  );
}

export function WannierExampleDetail({ ex }: { ex: Example }) {
  const { data: me } = useMe();
  const r = ex.result ?? {};
  return (
    <>
      <PageTitle title={ex.name}>
        <Button component={Link} to={examplesPath("tight-binding")} variant="default">All illustrative projects</Button>
      </PageTitle>
      <Stack gap="md" maw={1000}>
        <Card withBorder>
          <Stack gap={6}>
            <Text size="sm"><b>Material:</b> {ex.material} · <b>Wannier functions</b> (Tight Binding)
              {ex.dft_example && <> · DFT recipe of <Anchor component={Link} to={examplesPath("dft", ex.dft_example)}>{ex.dft_example}</Anchor></>}</Text>
            <Specialities items={ex.speciality} />
            <Text size="sm" c="dimmed">{ex.summary}</Text>
            <Alert color={r.converged ? "teal" : "yellow"} p="xs" data-testid="wannier-example-result">
              <Text size="sm"><b>Result:</b> {ex.expect}</Text>
              <Text size="xs" c="dimmed">{wannierResultLine(r)}{r.wannier90_version ? ` · Wannier90 ${r.wannier90_version}` : ""}
                {r.total_s ? ` · ${Math.round(r.total_s)} s from the DFT run` : ""}{r.warnings ? ` · ${r.warnings} warning${r.warnings > 1 ? "s" : ""} in its quality report` : ""}</Text>
            </Alert>
            <OpenButton ex={ex} />
            <Text size="xs" c="dimmed">Opening it costs no computing time: the DFT run and the Wannier run are copied into your project.</Text>
          </Stack>
        </Card>
        <Card withBorder data-testid="example-guide"><Markdown source={ex.guide ?? ""} /></Card>
        <Card withBorder>
          <TryList exampleId={ex.id} tries={ex.tries ?? []}
            note={`Each makes a new Wannier project with one thing changed, starting from this project's DFT run (its charge density is kept, so no new SCF).${
              me && !me.limits["wannier.qe_run"] ? " Running it needs the Advanced plan (Wannier90 from DFT); you can prepare and check it." : ""}`} />
        </Card>
      </Stack>
    </>
  );
}

/** In a Wannier project made from an illustrative project (or a "try this" of one): its guide and its variations. */
export function WannierExampleBanner({ exampleId, fromTry }: { exampleId: string; fromTry?: { title: string; about: string } }) {
  const { data: ex, isLoading } = useExample(exampleId);
  if (isLoading) return <Loader size="sm" />;
  if (fromTry)
    return (
      <Alert color="grape" p="xs" data-testid="wannier-from-example">
        <Text size="sm">Made from the illustrative project <Anchor component={Link} to={examplesPath("tight-binding", exampleId)}>{ex?.name ?? exampleId}</Anchor>:{" "}
          <b>{fromTry.title}</b> - {fromTry.about}</Text>
        <Text size="xs" c="dimmed">The DFT run is the illustrative project's (with its charge density): check the steps, run, and compare
          with the illustrative project's result{ex?.expect ? ` (${ex.expect})` : ""}.</Text>
      </Alert>
    );
  return (
    <Card withBorder p="sm" data-testid="wannier-example-banner">
      <Stack gap={6}>
        <Text size="sm"><b>Illustrative project</b>{ex ? `: ${ex.name}` : ""}. The DFT run and the finished Wannier run are in this project;
          look at the results, then go through the steps or try a variation.{" "}
          <Anchor component={Link} to={examplesPath("tight-binding", exampleId)} size="sm">All about it</Anchor></Text>
        {ex && <Accordion variant="contained" chevronPosition="left">
          <Accordion.Item value="guide">
            <Accordion.Control><Text size="sm">Guide: choices and what to look at</Text></Accordion.Control>
            <Accordion.Panel><Markdown source={ex.guide ?? ""} /></Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="try">
            <Accordion.Control><Text size="sm">Try this ({ex.tries?.length ?? 0})</Text></Accordion.Control>
            <Accordion.Panel><TryList exampleId={ex.id} tries={ex.tries ?? []}
              note="Each makes a new Wannier project with one thing changed, from this project's DFT run." /></Accordion.Panel>
          </Accordion.Item>
        </Accordion>}
      </Stack>
    </Card>
  );
}
